import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { FormatDate, FormatDateTimeBr } from '../../utils/date';
import { FormatDecimal1, FormatMoney } from '../../utils/format';
import { GetCampaignTypeLabel } from '../../constants/campaignTypes';
import { DASH, IsNum, Pill } from './campaignCardShared';

const money = (value) => (IsNum(value) ? FormatMoney(Number(value)) : DASH);
const pct = (value) => (IsNum(value) ? `${FormatDecimal1(Number(value))}%` : DASH);

const STATUS_LABEL = {
    started: 'participando',
    candidate: 'candidato',
    pending: 'pendente',
    finished: 'encerrado',
    saiu: 'saiu da campanha',
};

const ORIGEM_LABEL = {
    base: 'base',
    entrada: 'entrada',
    saida: 'saída',
    evento: 'evento',
};

const ORIGEM_TITLE = {
    base: 'Primeiro retrato desta participação na série de valores — ponto de partida, não mudança',
    entrada: 'Valor com que a campanha entrou no ar, vindo do log de eventos do sync',
    saida: 'Instante em que a participação deixou de existir no Mercado Livre, vindo do log de eventos do sync',
    evento: 'Mudança registrada no log de eventos, antes de a série de valores existir',
};

function MergeValorLinhas({ readings, periods }) {
    const porMinuto = new Map();
    const guarda = (linha) => {
        const chave = String(linha.at || '').slice(0, 16);
        if (!chave) {
            return;
        }
        const existente = porMinuto.get(chave);
        if (!existente || (existente.origem !== 'serie' && linha.origem === 'serie')) {
            porMinuto.set(chave, linha);
        }
    };

    for (const period of periods) {
        if (period.started_at) {
            guarda({
                at: period.started_at,
                origem: 'evento',
                marcador: 'entrada',
                status: 'started',
                consumer: period.opening_price,
                liquido: null,
                rebate_amount: null,
                rebate_percent: period.opening_meli_percentage,
                seller_percentage: period.opening_seller_percentage,
                discount_percent: null,
            });
        }
        for (const change of period.changes || []) {
            guarda({
                at: change.at,
                origem: 'evento',
                marcador: 'evento',
                status: 'started',
                consumer: change.price,
                liquido: null,
                rebate_amount: null,
                rebate_percent: change.meli_percentage,
                seller_percentage: change.seller_percentage,
                discount_percent: null,
            });
        }
        // A SAÍDA também é alteração: sem ela o histórico mostra a campanha
        // entrando duas vezes (18/09 e 19/09) e nunca diz que ela saiu no meio.
        if (period.ended_at && !period.ongoing && !period.end_unknown) {
            guarda({
                at: period.ended_at,
                origem: 'evento',
                marcador: 'saida',
                status: 'saiu',
                consumer: null,
                liquido: null,
                rebate_amount: null,
                rebate_percent: null,
                seller_percentage: null,
                discount_percent: null,
            });
        }
    }

    for (const reading of readings) {
        guarda({
            at: reading.captured_at,
            origem: 'serie',
            marcador: reading.baseline ? 'base' : null,
            status: reading.item_status,
            sugerido: reading.price_is_suggested === true,
            faixa:
                IsNum(reading.price_min) || IsNum(reading.price_max)
                    ? `Faixa que o ML permite: ${money(reading.price_min)} a ${money(reading.price_max)}`
                    : null,
            consumer: reading.price_consumer_final ?? reading.price_consumer,
            liquido: reading.price_net_with_boost ?? reading.price_net,
            rebate_amount: reading.rebate_amount,
            rebate_percent: reading.rebate_percent,
            seller_percentage: reading.seller_percentage,
            discount_percent: reading.discount_percent,
        });
    }

    return [...porMinuto.values()].sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
}

const ValoresTable = ({ linhas }) => (
    <div className="mt-1.5 overflow-x-auto rounded-lg border border-mono-100 dark:border-mono-900">
        <table className="w-full min-w-[560px] text-[11px]">
            <thead className="bg-mono-50 text-left uppercase tracking-wide text-mono-500 dark:bg-mono-900 dark:text-mono-400">
                <tr>
                    <th className="px-2 py-1 font-semibold">Leitura</th>
                    <th className="px-2 py-1 font-semibold">Status</th>
                    <th className="px-2 py-1 text-right font-semibold">Consumer</th>
                    <th className="px-2 py-1 text-right font-semibold">Líquido</th>
                    <th className="px-2 py-1 text-right font-semibold">Rebate</th>
                    <th className="px-2 py-1 text-right font-semibold">Sua parte</th>
                    <th className="px-2 py-1 text-right font-semibold">Desc%</th>
                </tr>
            </thead>
            <tbody>
                {linhas.map((linha, idx) => (
                    <tr
                        key={`${linha.at}-${idx}`}
                        className="border-t border-mono-100 tabular-nums dark:border-mono-900"
                    >
                        <td className="px-2 py-1 font-mono text-mono-600 dark:text-mono-400">
                            {FormatDateTimeBr(linha.at)}
                            {linha.marcador ? (
                                <span
                                    className="ml-1 font-sans text-[10px] text-mono-400"
                                    title={ORIGEM_TITLE[linha.marcador]}
                                >
                                    {ORIGEM_LABEL[linha.marcador]}
                                </span>
                            ) : null}
                        </td>
                        <td className="px-2 py-1">{STATUS_LABEL[linha.status] || linha.status || DASH}</td>
                        <td className="px-2 py-1 text-right font-mono" title={linha.faixa || undefined}>
                            {linha.sugerido && IsNum(linha.consumer) ? (
                                <span
                                    className="mr-0.5 text-mono-400"
                                    title="Preço SUGERIDO pelo ML para esta candidatura — não é oferta acordada"
                                >
                                    ~
                                </span>
                            ) : null}
                            {money(linha.consumer)}
                        </td>
                        <td className="px-2 py-1 text-right font-mono font-semibold">{money(linha.liquido)}</td>
                        <td className="px-2 py-1 text-right font-mono">
                            {money(linha.rebate_amount)}
                            {IsNum(linha.rebate_percent) ? (
                                <span className="ml-1 text-[10px] text-mono-400">{pct(linha.rebate_percent)}</span>
                            ) : null}
                        </td>
                        <td className="px-2 py-1 text-right font-mono">{pct(linha.seller_percentage)}</td>
                        <td className="px-2 py-1 text-right font-mono">{pct(linha.discount_percent)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const CampaignBlock = ({ campaign, observationInterval }) => {
    const readings = useMemo(() => campaign.readings || [], [campaign.readings]);
    const periods = useMemo(() => campaign.periods || [], [campaign.periods]);
    // Entradas/saídas (eventos) e leituras de valor, costuradas numa tabela só:
    // é o histórico de alterações desta campanha para este anúncio, sem
    // resumo nem status por cima — quem está valendo é assunto da aba Linha
    // do tempo (só uma vale por vez; aqui várias podem estar "participando").
    const linhas = useMemo(() => MergeValorLinhas({ readings, periods }), [readings, periods]);

    return (
        <li className="rounded-xl border border-mono-200 bg-white p-3 dark:border-mono-800 dark:bg-mono-950">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="min-w-0 flex-1 truncate text-xs font-semibold text-black dark:text-white">
                    {campaign.campaign_name || campaign.campaign_id}
                    <span className="ml-1 font-mono text-[10px] font-normal text-mono-400">{campaign.campaign_id}</span>
                    {campaign.campaign_type ? (
                        <span className="ml-1.5 text-[10px] font-normal text-mono-500 dark:text-mono-400">
                            {GetCampaignTypeLabel(campaign.campaign_type)}
                        </span>
                    ) : null}
                </span>
                {campaign.campaign_finish_date ? (
                    <span
                        className="shrink-0 rounded-full border border-mono-200 px-2 py-px text-[10px] text-mono-600 dark:border-mono-800 dark:text-mono-400"
                        title={`Data de término que o Mercado Livre informa para esta campanha (${FormatDateTimeBr(campaign.campaign_finish_date)}). Exibição apenas: as datas do histórico vêm do que o sync observou.`}
                    >
                        ML diz que expira em {FormatDate(campaign.campaign_finish_date)}
                    </span>
                ) : null}
                {campaign.divergent ? (
                    <Pill tone="danger" title="Na ativação, o aplicado diferiu do registrado na candidatura">
                        divergente
                    </Pill>
                ) : null}
            </div>

            {linhas.length > 0 ? (
                <ValoresTable linhas={linhas} />
            ) : (
                <p className="mt-1.5 text-[11px] italic text-mono-500 dark:text-mono-400">
                    Nenhuma alteração registrada. O sync confere a cada {observationInterval} min e só grava quando
                    algum valor muda.
                </p>
            )}
        </li>
    );
};

const CampaignItemTracking = ({ itemId }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    const carregar = useCallback(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        api.getItemTracking(itemId)
            .then((res) => {
                if (!cancelled) {
                    setData(res);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err?.message || 'Não foi possível carregar o acompanhamento.');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [itemId]);

    useEffect(() => carregar(), [carregar]);

    if (loading) {
        return <p className="px-1 text-[11px] text-mono-500 dark:text-mono-400">Carregando acompanhamento…</p>;
    }
    if (error) {
        return <p className="px-1 text-[11px] text-danger-600 dark:text-danger-400">{error}</p>;
    }

    const campaigns = data?.campaigns || [];
    const intervalo = Number(data?.observation_interval_minutes || 0);

    if (campaigns.length === 0) {
        return (
            <p className="px-1 text-[11px] italic text-mono-500 dark:text-mono-400">
                Nenhuma campanha conhecida para este anúncio.
            </p>
        );
    }

    return (
        <div className="space-y-2">
            <p className="px-1 text-[11px] text-mono-600 dark:text-mono-400">
                <strong className="text-black dark:text-white">{campaigns.length}</strong>{' '}
                {campaigns.length === 1 ? 'campanha' : 'campanhas'} · histórico de alterações; um registro novo só
                quando algum valor muda
            </p>

            <ol className="space-y-2">
                {campaigns.map((campaign) => (
                    <CampaignBlock key={campaign.campaign_id} campaign={campaign} observationInterval={intervalo} />
                ))}
            </ol>

            <p className="px-1 text-[10px] text-mono-500 dark:text-mono-400">
                Datas são o instante em que o sync observou a mudança
                {intervalo > 0 ? `, e ele confere a cada ${intervalo} min` : ''}.
                {data?.readings_truncated ? ' Leitura cortada no teto: mudanças mais antigas podem faltar.' : ''}
            </p>
        </div>
    );
};

export default CampaignItemTracking;
